import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Loader2, User, Dumbbell, Heart, Activity, Trophy } from "lucide-react";

// Helper: converteste "dd.mm.yyyy" (format RO) → "yyyy-mm-dd" (ISO) pentru backend.
const roDateToIso = (v) => {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(v || "").trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : String(v || "");
};

// Helper: converteste "yyyy-mm-dd" (ISO) → "dd.mm.yyyy" (format RO) pentru afișare.
const isoDateToRo = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || "").trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(v || "");
};

// Validare dată calendaristică reală (nu doar format).
const isValidRoDate = (v) => {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(v || "").trim());
  if (!m) return false;
  const [, d, mo, y] = m;
  const dn = Number(d), mn = Number(mo), yn = Number(y);
  if (yn < 1900 || yn > 2100 || mn < 1 || mn > 12 || dn < 1 || dn > 31) return false;
  const dt = new Date(yn, mn - 1, dn);
  return dt.getFullYear() === yn && dt.getMonth() === mn - 1 && dt.getDate() === dn;
};

const patientSchema = z.object({
  date_personale: z.object({
    nume: z.string()
      .transform((v) => String(v || "").trim())
      .refine((v) => v.length >= 2, "Numele este obligatoriu (minim 2 litere)"),
    prenume: z.string()
      .transform((v) => String(v || "").trim())
      .refine((v) => v.length >= 2, "Prenumele este obligatoriu (minim 2 litere)"),
    data_nasterii: z.string()
      .min(1, "Data nașterii este obligatorie")
      .refine(isValidRoDate, "Format invalid — folosește zz.ll.aaaa (ex: 15.05.2010)")
      .transform(roDateToIso),
    telefon: z.string()
      .transform((v) => String(v || "").replace(/\D/g, ""))
      .refine((v) => v.length >= 6 && v.length <= 15, "Telefonul trebuie să aibă între 6 și 15 cifre"),
    sex: z.string().min(1, "Sexul este obligatoriu"),
    cnp: z.string().regex(/^\d{13}$/, "CNP-ul trebuie să conțină exact 13 cifre"),
    localitate: z.string().min(1, "Localitatea este obligatorie"),
    seniori: z.boolean().optional(),
    juniori: z.boolean().optional(),
  }).superRefine((dp, ctx) => {
    // Validare încrucișată CNP ↔ (data nașterii + sex).
    // După transform(), data_nasterii este ISO (YYYY-MM-DD), iar telefonul e curățat de non-digits.
    const cnp = String(dp.cnp || "");
    if (!/^\d{13}$/.test(cnp)) return; // deja semnalat de regex-ul principal
    const dob = String(dp.data_nasterii || "");
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
    if (!isoMatch) return; // data invalidă — validarea de mai sus a marcat deja
    const [, yStr, mStr, dStr] = isoMatch;
    const cnpMsg = "CNP-ul nu corespunde cu datele personale, verificați datele introduse";

    const firstDigit = cnp[0];
    // Sex din CNP
    const cnpSexMale = firstDigit === "1" || firstDigit === "3" || firstDigit === "5" || firstDigit === "7";
    const cnpSexFemale = firstDigit === "2" || firstDigit === "4" || firstDigit === "6" || firstDigit === "8";
    if (!cnpSexMale && !cnpSexFemale) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["cnp"], message: cnpMsg });
      return;
    }
    if (dp.sex === "Masculin" && !cnpSexMale) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["cnp"], message: cnpMsg });
      return;
    }
    if (dp.sex === "Feminin" && !cnpSexFemale) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["cnp"], message: cnpMsg });
      return;
    }

    // Secolul din CNP: 1/2 → 1900s, 3/4 → 1800s, 5/6 → 2000s, 7/8 → rezidenți străini
    // (pentru 7/8 sărim verificarea anului pentru că regulile sunt mai complexe)
    let expectedCentury = null;
    if (firstDigit === "1" || firstDigit === "2") expectedCentury = 1900;
    else if (firstDigit === "3" || firstDigit === "4") expectedCentury = 1800;
    else if (firstDigit === "5" || firstDigit === "6") expectedCentury = 2000;

    const yy = cnp.slice(1, 3);
    const mm = cnp.slice(3, 5);
    const dd = cnp.slice(5, 7);

    if (expectedCentury !== null) {
      const expectedYear = String(expectedCentury + parseInt(yy, 10));
      if (expectedYear !== yStr) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["cnp"], message: cnpMsg });
        return;
      }
    }
    if (mm !== mStr) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["cnp"], message: cnpMsg });
      return;
    }
    if (dd !== dStr) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["cnp"], message: cnpMsg });
      return;
    }
  }),
  info_sportive: z.object({
    sport_practicat: z.string().min(1, "Sportul practicat este obligatoriu"),
    post_categorie: z.string().optional(),
    club_sportiv: z.string().min(1, "Clubul sportiv este obligatoriu"),
    antrenor: z.string().min(1, "Numele antrenorului este obligatoriu"),
    antrenamente_saptamana: z.string().min(1, "Numărul de antrenamente pe săptămână este obligatoriu"),
  }),
  istoric_medical: z.object({
    antecedente_familie: z.string().min(1, "Selectați Da sau Nu"),
    antecedente_familie_detalii: z.string().optional(),
    antecedente_familie_tipuri: z.array(z.string()).optional(),
    antecedente_personale: z.string().min(1, "Selectați Da sau Nu"),
    antecedente_personale_detalii: z.string().optional(),
    modificari_sanatate: z.string().min(1, "Selectați Da sau Nu"),
    modificari_sanatate_detalii: z.string().optional(),
    accidentari_sportive: z.string().min(1, "Selectați Da sau Nu"),
    accidentari_sportive_detalii: z.string().optional(),
    internari_medicale: z.string().min(1, "Selectați Da sau Nu"),
    internari_medicale_detalii: z.string().optional(),
    tratament_medicamentos: z.string().min(1, "Selectați Da sau Nu"),
    tratament_detalii: z.string().optional(),
  }),
  efort_fizic: z.object({
    schimbari_efort: z.string().min(1, "Selectați Da sau Nu"),
    schimbari_efort_detalii: z.string().optional(),
    schimbari_efort_tipuri: z.array(z.string()).optional(),
    toleranta_efort: z.string().optional(),
    suplimente_nutritive: z.string().min(1, "Selectați Da sau Nu"),
    oboseste_repede: z.string().min(1, "Selectați Da sau Nu"),
  }),
  info_seniori: z.object({
    vechime_sportiva: z.string().optional(),
    alte_sporturi: z.string().optional(),
    alte_sporturi_detalii: z.string().optional(),
    ultimul_club: z.string().optional(),
    ultimul_control: z.string().optional(),
    tulburari_apetit: z.string().optional(),
    tulburari_apetit_detalii: z.string().optional(),
  }).optional(),
  declaratii: z.object({
    declaratie_boli: z.literal(true, {
      errorMap: () => ({ message: "Trebuie să confirmați declarația privind starea de sănătate" }),
    }),
    acord_date_personale: z.literal(true, {
      errorMap: () => ({ message: "Trebuie să acordați consimțământul pentru prelucrarea datelor personale" }),
    }),
    cunostinta_anulare: z.literal(true, {
      errorMap: () => ({ message: "Trebuie să confirmați luarea la cunoștință a clauzei de anulare" }),
    }),
  }),
});

// Opțiuni pentru simptomele care apar în timpul sau imediat după efort.
// Se afișează ca listă de checkboxuri când "schimbari_efort" == "Da".
// Bifarea "Altele" declanșează câmpul liber "Specificați schimbările".
const SCHIMBARI_EFORT_OPTIONS = [
  "Sincopă / presincopă (pierderea stării de conștiență în timpul efortului sau imediat după)",
  "Discomfort toracic (durere, presiune în piept în timpul efortului sau imediat după)",
  "Palpitații (bătăi neregulate ale inimii în timpul efortului sau imediat după)",
  "Oboseală marcată (disproporționată cu efortul depus)",
  "Dureri de cap",
  "Amețeli",
  "Dureri abdominale",
  "Grețuri, vărsături",
  "Dureri articulare",
  "Dureri musculare",
  "Altele",
];

// Opțiuni pentru antecedente medicale în familie (părinți, frați, surori)
// Se afișează ca listă de checkboxuri când "antecedente_familie" == "Da".
// Bifarea "Altele" declanșează câmpul liber "Specificați antecedentele medicale din familie".
const ANTECEDENTE_FAMILIE_OPTIONS = [
  "Moarte subită cardiacă (rude gradul 1 sau 2, decedate subit sub vârsta de 50 de ani)",
  "Boli cardiace cunoscute (cardiomiopatie hipertrofică, cardiomiopatie dilatativă, sindrom QT lung)",
  "Altele",
];

const defaultValues = {
  date_personale: {
    nume: "",
    prenume: "",
    data_nasterii: "",
    telefon: "",
    sex: "",
    cnp: "",
    localitate: "",
    seniori: false,
    juniori: false,
  },
  info_sportive: {
    sport_practicat: "",
    post_categorie: "",
    club_sportiv: "",
    antrenor: "",
    antrenamente_saptamana: "",
  },
  istoric_medical: {
    antecedente_familie: "Nu",
    antecedente_familie_detalii: "",
    antecedente_familie_tipuri: [],
    antecedente_personale: "Nu",
    antecedente_personale_detalii: "",
    modificari_sanatate: "Nu",
    modificari_sanatate_detalii: "",
    accidentari_sportive: "Nu",
    accidentari_sportive_detalii: "",
    internari_medicale: "Nu",
    internari_medicale_detalii: "",
    tratament_medicamentos: "Nu",
    tratament_detalii: "",
  },
  efort_fizic: {
    schimbari_efort: "Nu",
    schimbari_efort_detalii: "",
    schimbari_efort_tipuri: [],
    toleranta_efort: "",
    suplimente_nutritive: "Nu",
    oboseste_repede: "Nu",
  },
  info_seniori: {
    vechime_sportiva: "",
    alte_sporturi: "",
    alte_sporturi_detalii: "",
    ultimul_club: "",
    ultimul_control: "",
    tulburari_apetit: "",
    tulburari_apetit_detalii: "",
  },
  declaratii: {
    declaratie_boli: false,
    acord_date_personale: false,
    cunostinta_anulare: false,
  },
};

export const PatientForm = ({
  onSubmit,
  initialData = null,
  isLoading = false,
  submitLabel = "Salvează Sportiv",
  publicMode = false,
  attachmentsSlot = null,
}) => {
  // Convertim data nașterii ISO din initialData în format RO (dd.mm.yyyy) pentru afișare.
  const normalizedInitialData = initialData
    ? {
        ...initialData,
        date_personale: {
          ...(initialData.date_personale || {}),
          data_nasterii: isoDateToRo(initialData.date_personale?.data_nasterii || ""),
        },
      }
    : null;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(patientSchema),
    defaultValues: normalizedInitialData || defaultValues,
  });

  const sex = watch("date_personale.sex") || "";
  const seniori = watch("date_personale.seniori");
  const juniori = watch("date_personale.juniori");
  const alteSporturi = watch("info_seniori.alte_sporturi");
  const tulburariApetit = watch("info_seniori.tulburari_apetit");
  const tratament = watch("istoric_medical.tratament_medicamentos");
  const modificariSanatate = watch("istoric_medical.modificari_sanatate");
  const schimbariEfort = watch("efort_fizic.schimbari_efort");
  const accidentariSportive = watch("istoric_medical.accidentari_sportive");
  const internariMedicale = watch("istoric_medical.internari_medicale");
  const antecedenteFamilie = watch("istoric_medical.antecedente_familie");
  const antecedentePersonale = watch("istoric_medical.antecedente_personale");

  // Live-check CNP vs (data nașterii + sex) — verifică în timp real, fără a aștepta submit-ul.
  // Regulile sunt aceleași ca în schema Zod (superRefine pe date_personale).
  const cnpLive = watch("date_personale.cnp") || "";
  const dataNasteriiLive = watch("date_personale.data_nasterii") || ""; // format RO zz.ll.aaaa înainte de submit
  const cnpMismatchMsg = (() => {
    if (!/^\d{13}$/.test(cnpLive)) return "";
    const first = cnpLive[0];
    // Sex
    const male = ["1", "3", "5", "7"].includes(first);
    const female = ["2", "4", "6", "8"].includes(first);
    if (!male && !female) return "CNP-ul nu corespunde cu datele personale, verificați datele introduse";
    if (sex === "Masculin" && !male) return "CNP-ul nu corespunde cu datele personale, verificați datele introduse";
    if (sex === "Feminin" && !female) return "CNP-ul nu corespunde cu datele personale, verificați datele introduse";
    // Data nașterii — se așteaptă format RO zz.ll.aaaa
    const dobMatch = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(dataNasteriiLive);
    if (!dobMatch) return "";
    const [, dd, mm, yyyy] = dobMatch;
    let expectedCentury = null;
    if (first === "1" || first === "2") expectedCentury = 1900;
    else if (first === "3" || first === "4") expectedCentury = 1800;
    else if (first === "5" || first === "6") expectedCentury = 2000;
    if (expectedCentury !== null) {
      const expectedYear = String(expectedCentury + parseInt(cnpLive.slice(1, 3), 10));
      if (expectedYear !== yyyy) return "CNP-ul nu corespunde cu datele personale, verificați datele introduse";
    }
    if (cnpLive.slice(3, 5) !== mm) return "CNP-ul nu corespunde cu datele personale, verificați datele introduse";
    if (cnpLive.slice(5, 7) !== dd) return "CNP-ul nu corespunde cu datele personale, verificați datele introduse";
    return "";
  })();

  // Aplatizare recursivă a erorilor Zod în mesaje cu etichete prietenoase
  const flattenErrors = (errObj, parent = "") => {
    const labels = {
      "date_personale.nume": "Nume",
      "date_personale.prenume": "Prenume",
      "date_personale.data_nasterii": "Data nașterii",
      "date_personale.telefon": "Telefon",
      "date_personale.sex": "Sex",
      "date_personale.cnp": "CNP",
      "date_personale.localitate": "Localitate",
      "info_sportive.sport_practicat": "Sport practicat",
      "info_sportive.club_sportiv": "Club sportiv",
      "info_sportive.antrenor": "Antrenor",
      "info_sportive.antrenamente_saptamana": "Antrenamente / săptămână",
      "istoric_medical.antecedente_familie": "Antecedente medicale în familie",
      "istoric_medical.antecedente_personale": "Antecedente medicale personale",
      "istoric_medical.modificari_sanatate": "Modificări stare sănătate",
      "istoric_medical.accidentari_sportive": "Accidentări sportive",
      "istoric_medical.internari_medicale": "Internări medicale",
      "istoric_medical.tratament_medicamentos": "Tratament medicamentos",
      "efort_fizic.schimbari_efort": "Schimbări la efort",
      "efort_fizic.suplimente_nutritive": "Suplimente nutritive",
      "efort_fizic.oboseste_repede": "Obosește repede la efort",
      "declaratii.declaratie_boli": "Declarație stare sănătate (boli)",
      "declaratii.acord_date_personale": "Acord prelucrare date personale (GDPR)",
      "declaratii.cunostinta_anulare": "Luare la cunoștință clauza anulare",
    };
    const out = [];
    Object.entries(errObj || {}).forEach(([key, val]) => {
      const path = parent ? `${parent}.${key}` : key;
      if (val && typeof val === "object" && !val.message && !val.type) {
        out.push(...flattenErrors(val, path));
      } else if (val && val.message) {
        out.push({ field: labels[path] || path, message: val.message });
      }
    });
    return out;
  };

  const errorSummary = flattenErrors(errors);

  // Sufix unic per montare pentru a anula heuristica de match a browser-ului (managere de parole, formfill)
  // În admin mode rămâne empty deci nu afectează autocomplete existent.
  const noFillSuffix = publicMode ? `-nf${Math.random().toString(36).slice(2, 9)}` : "";

  return (
    <form
      onSubmit={handleSubmit(onSubmit, () => {
        // Scroll la sumar la submit invalid
        setTimeout(() => {
          const summary = document.querySelector('[data-testid="form-error-summary"]');
          if (summary) summary.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      })}
      className="space-y-6"
      autoComplete={publicMode ? "off" : "on"}
      {...(publicMode ? { "data-form-type": "other" } : {})}
    >
      {/* Honeypot anti-autofill (browserele Chrome/Edge ignoră autoComplete="off"
          dacă nu există un input fake care să consume sugestiile) */}
      {publicMode && (
        <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden="true">
          <input type="text" name="username_fake" tabIndex="-1" autoComplete="username" />
          <input type="password" name="password_fake" tabIndex="-1" autoComplete="current-password" />
        </div>
      )}
      {/* Date Personale */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            <User className="w-5 h-5 text-primary" />
            Date Personale
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nume">Nume *</Label>
              <Input
                id={`nume${noFillSuffix}`}
                data-testid="input-nume"
                placeholder="Introduceți numele"
                autoComplete={publicMode ? "off" : "family-name"}
                data-lpignore={publicMode ? "true" : undefined}
                data-1p-ignore={publicMode ? "true" : undefined}
                {...register("date_personale.nume")}
              />
              {errors.date_personale?.nume && (
                <p className="text-sm text-destructive">{errors.date_personale.nume.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="prenume">Prenume *</Label>
              <Input
                id={`prenume${noFillSuffix}`}
                data-testid="input-prenume"
                placeholder="Introduceți prenumele"
                autoComplete={publicMode ? "off" : "given-name"}
                data-lpignore={publicMode ? "true" : undefined}
                data-1p-ignore={publicMode ? "true" : undefined}
                {...register("date_personale.prenume")}
              />
              {errors.date_personale?.prenume && (
                <p className="text-sm text-destructive">{errors.date_personale.prenume.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="data_nasterii">Data Nașterii</Label>
              <Input
                id={`data_nasterii${noFillSuffix}`}
                data-testid="input-data-nasterii"
                type="text"
                inputMode="numeric"
                maxLength={10}
                placeholder="zz.ll.aaaa"
                autoComplete={publicMode ? "off" : "bday"}
                data-lpignore={publicMode ? "true" : undefined}
                data-1p-ignore={publicMode ? "true" : undefined}
                {...register("date_personale.data_nasterii", {
                  onChange: (e) => {
                    // Auto-format zz.ll.aaaa: acceptăm doar cifre + inserăm punct după poziția 2 și 5.
                    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 8);
                    let out = digits;
                    if (digits.length >= 5) {
                      out = `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
                    } else if (digits.length >= 3) {
                      out = `${digits.slice(0, 2)}.${digits.slice(2)}`;
                    }
                    if (out !== e.target.value) e.target.value = out;
                  },
                })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="telefon">Număr de Telefon</Label>
              <Input
                id={`telefon${noFillSuffix}`}
                data-testid="input-telefon"
                type="text"
                inputMode="numeric"
                maxLength={15}
                placeholder="07XXXXXXXX"
                autoComplete={publicMode ? "off" : "tel"}
                data-lpignore={publicMode ? "true" : undefined}
                data-1p-ignore={publicMode ? "true" : undefined}
                {...register("date_personale.telefon", {
                  onChange: (e) => {
                    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 15);
                    if (digits !== e.target.value) e.target.value = digits;
                  },
                })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="sex">Sex *</Label>
              <div
                className="inline-flex rounded-md border overflow-hidden w-full"
                role="radiogroup"
                aria-label="Sex"
                data-testid="select-sex"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={sex === "Masculin"}
                  onClick={() => setValue("date_personale.sex", "Masculin", { shouldValidate: true, shouldDirty: true })}
                  className={`flex-1 px-3 py-2 text-sm font-medium transition ${
                    sex === "Masculin"
                      ? "bg-blue-600 text-white"
                      : "bg-white hover:bg-blue-50 text-blue-700"
                  }`}
                  data-testid="sex-masculin"
                >
                  Masculin
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={sex === "Feminin"}
                  onClick={() => setValue("date_personale.sex", "Feminin", { shouldValidate: true, shouldDirty: true })}
                  className={`flex-1 px-3 py-2 text-sm font-medium border-l transition ${
                    sex === "Feminin"
                      ? "bg-pink-600 text-white"
                      : "bg-white hover:bg-pink-50 text-pink-700"
                  }`}
                  data-testid="sex-feminin"
                >
                  Feminin
                </button>
              </div>
              {errors.date_personale?.sex && (
                <p className="text-sm text-destructive">{errors.date_personale.sex.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="cnp">CNP</Label>
              <Input
                id={`cnp${noFillSuffix}`}
                data-testid="input-cnp"
                placeholder="13 cifre"
                autoComplete="off"
                data-lpignore={publicMode ? "true" : undefined}
                data-1p-ignore={publicMode ? "true" : undefined}
                inputMode="numeric"
                maxLength={13}
                {...register("date_personale.cnp", {
                  onChange: (e) => {
                    // Permitem doar cifre; taie orice altceva (litere, spații, semne)
                    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 13);
                    if (digits !== e.target.value) e.target.value = digits;
                  },
                })}
              />
              {errors.date_personale?.cnp && (
                <p className="text-sm text-destructive" data-testid="cnp-error">{errors.date_personale.cnp.message}</p>
              )}
              {!errors.date_personale?.cnp && cnpMismatchMsg && (
                <p className="text-sm text-destructive" data-testid="cnp-live-mismatch">{cnpMismatchMsg}</p>
              )}
            </div>

            <div className="space-y-2 md:col-span-2 lg:col-span-3">
              <Label htmlFor="localitate">Localitatea</Label>
              <Input
                id={`localitate${noFillSuffix}`}
                data-testid="input-localitate"
                placeholder="Orașul / Comuna"
                autoComplete={publicMode ? "off" : "address-level2"}
                data-lpignore={publicMode ? "true" : undefined}
                data-1p-ignore={publicMode ? "true" : undefined}
                {...register("date_personale.localitate")}
              />
            </div>

            <div className="md:col-span-2 lg:col-span-3 flex flex-wrap gap-6 pt-2 border-t border-slate-200">
              <Label className="text-sm font-medium text-slate-700 w-full mb-1">
                Categorie sportivă
              </Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="seniori"
                  data-testid="checkbox-seniori"
                  checked={seniori || false}
                  onCheckedChange={(checked) => setValue("date_personale.seniori", !!checked)}
                />
                <Label htmlFor="seniori" className="text-sm cursor-pointer font-normal">
                  Seniori
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="juniori"
                  data-testid="checkbox-juniori"
                  checked={juniori || false}
                  onCheckedChange={(checked) => setValue("date_personale.juniori", !!checked)}
                />
                <Label htmlFor="juniori" className="text-sm cursor-pointer font-normal">
                  Juniori
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Sportive */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            <Dumbbell className="w-5 h-5 text-primary" />
            Informații Sportive
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sport">Sportul Practicat</Label>
              <Input
                id="sport"
                data-testid="input-sport"
                placeholder="ex: Fotbal, Atletism"
                {...register("info_sportive.sport_practicat")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="post">Postul / Categoria Sportivă</Label>
              <Input
                id="post"
                data-testid="input-post"
                placeholder="ex: Atacant, Juniori"
                {...register("info_sportive.post_categorie")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="club">Clubul Sportiv</Label>
              <Input
                id="club"
                data-testid="input-club"
                placeholder="Numele clubului"
                {...register("info_sportive.club_sportiv")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="antrenor">Antrenor</Label>
              <Input
                id="antrenor"
                data-testid="input-antrenor"
                placeholder="Numele antrenorului"
                {...register("info_sportive.antrenor")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="antrenamente">Antrenamente pe Săptămână</Label>
              <Input
                id="antrenamente"
                data-testid="input-antrenamente"
                placeholder="ex: 5"
                {...register("info_sportive.antrenamente_saptamana")}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Istoric Medical */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            <Heart className="w-5 h-5 text-primary" />
            Istoric Medical
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Antecedente medicale în familie - Da/Nu + checkboxuri specifice */}
          <div className="space-y-2">
            <Label htmlFor="antecedente_familie">Antecedente medicale în familie (părinți, frați, surori)</Label>
            <div
              className="inline-flex rounded-md border overflow-hidden"
              role="radiogroup"
              aria-label="Antecedente familie"
              data-testid="select-antecedente-familie"
            >
              <button
                type="button"
                role="radio"
                aria-checked={antecedenteFamilie === "Nu"}
                onClick={() => setValue("istoric_medical.antecedente_familie", "Nu", { shouldValidate: true, shouldDirty: true })}
                className={`px-4 py-2 text-sm font-medium transition ${antecedenteFamilie === "Nu" ? "bg-slate-700 text-white" : "bg-white hover:bg-slate-50 text-slate-700"}`}
                data-testid="antecedente-familie-nu"
              >
                Nu
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={antecedenteFamilie === "Da"}
                onClick={() => setValue("istoric_medical.antecedente_familie", "Da", { shouldValidate: true, shouldDirty: true })}
                className={`px-4 py-2 text-sm font-medium border-l transition ${antecedenteFamilie === "Da" ? "bg-amber-600 text-white" : "bg-white hover:bg-amber-50 text-amber-700"}`}
                data-testid="antecedente-familie-da"
              >
                Da
              </button>
            </div>
          </div>
          {antecedenteFamilie === "Da" && (
            <div className="space-y-3 pl-4 border-l-2 border-primary/30" data-testid="antecedente-familie-tipuri-container">
              <p className="text-sm font-medium">Ce tip de antecedente?</p>
              <div className="space-y-2">
                {ANTECEDENTE_FAMILIE_OPTIONS.map((opt) => {
                  const tipuri = watch("istoric_medical.antecedente_familie_tipuri") || [];
                  const checked = tipuri.includes(opt);
                  const idAttr = `antecedente-familie-opt-${opt.substring(0, 20).replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`;
                  return (
                    <div key={opt} className="flex items-start gap-2">
                      <Checkbox
                        id={idAttr}
                        checked={checked}
                        onCheckedChange={(v) => {
                          const current = watch("istoric_medical.antecedente_familie_tipuri") || [];
                          const isOn = !!v;
                          const next = isOn
                            ? [...current.filter((x) => x !== opt), opt]
                            : current.filter((x) => x !== opt);
                          setValue("istoric_medical.antecedente_familie_tipuri", next, { shouldDirty: true });
                          if (opt === "Altele" && !isOn) {
                            setValue("istoric_medical.antecedente_familie_detalii", "", { shouldDirty: true });
                          }
                        }}
                        data-testid={idAttr}
                      />
                      <Label htmlFor={idAttr} className="text-sm font-normal leading-snug cursor-pointer">
                        {opt}
                      </Label>
                    </div>
                  );
                })}
              </div>
              {(watch("istoric_medical.antecedente_familie_tipuri") || []).includes("Altele") && (
                <div className="space-y-1 pt-1" data-testid="antecedente-familie-detalii-container">
                  <Label htmlFor="antecedente_familie_detalii" className="text-sm">Specificați antecedentele medicale din familie</Label>
                  <Textarea
                    id="antecedente_familie_detalii"
                    data-testid="input-antecedente-familie-detalii"
                    placeholder=""
                    rows={2}
                    {...register("istoric_medical.antecedente_familie_detalii")}
                  />
                </div>
              )}
            </div>
          )}

          {/* Antecedente medicale personale - Da/Nu + Specificați */}
          <div className="space-y-2">
            <Label htmlFor="antecedente_personale">Antecedente medicale personale</Label>
            <Select
              value={antecedentePersonale}
              onValueChange={(value) => setValue("istoric_medical.antecedente_personale", value)}
            >
              <SelectTrigger data-testid="select-antecedente-personale">
                <SelectValue placeholder="Selectați" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Nu">Nu</SelectItem>
                <SelectItem value="Da">Da</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {antecedentePersonale === "Da" && (
            <div className="space-y-2 pl-4 border-l-2 border-primary/30">
              <Label htmlFor="antecedente_personale_detalii">Specificați antecedentele personale</Label>
              <Textarea
                id="antecedente_personale_detalii"
                data-testid="input-antecedente-personale-detalii"
                placeholder=""
                rows={2}
                {...register("istoric_medical.antecedente_personale_detalii")}
              />
            </div>
          )}

          {/* Modificări stare sănătate */}
          <div className="space-y-2">
            <Label htmlFor="modificari_sanatate">
              De la ultimul control medical și până în prezent ați avut modificări ale stării de sănătate?
            </Label>
            <Select
              value={modificariSanatate}
              onValueChange={(value) => setValue("istoric_medical.modificari_sanatate", value)}
            >
              <SelectTrigger data-testid="select-modificari">
                <SelectValue placeholder="Selectați" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Nu">Nu</SelectItem>
                <SelectItem value="Da">Da</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {modificariSanatate === "Da" && (
            <div className="space-y-2 pl-4 border-l-2 border-primary/30">
              <Label htmlFor="modificari_sanatate_detalii">Specificați modificările stării de sănătate</Label>
              <Textarea
                id="modificari_sanatate_detalii"
                data-testid="input-modificari-detalii"
                placeholder="ex: dureri toracice la efort, dispnee crescută"
                rows={2}
                {...register("istoric_medical.modificari_sanatate_detalii")}
              />
            </div>
          )}

          {/* Accidentări sportive */}
          <div className="space-y-2">
            <Label htmlFor="accidentari_sportive">
              De la ultimul control medical (ultimele 6 luni) ați avut accidentări în activitatea sportivă?
            </Label>
            <Select
              value={accidentariSportive}
              onValueChange={(value) => setValue("istoric_medical.accidentari_sportive", value)}
            >
              <SelectTrigger data-testid="select-accidentari">
                <SelectValue placeholder="Selectați" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Nu">Nu</SelectItem>
                <SelectItem value="Da">Da</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {accidentariSportive === "Da" && (
            <div className="space-y-2 pl-4 border-l-2 border-primary/30">
              <Label htmlFor="accidentari_sportive_detalii">Specificați accidentările sportive</Label>
              <Textarea
                id="accidentari_sportive_detalii"
                data-testid="input-accidentari-detalii"
                placeholder="ex: entorsă gleznă dreaptă în martie 2026"
                rows={2}
                {...register("istoric_medical.accidentari_sportive_detalii")}
              />
            </div>
          )}

          {/* Internări medicale */}
          <div className="space-y-2">
            <Label htmlFor="internari_medicale">
              De la ultimul control medical (ultimele 6 luni) ați fost internat într-o unitate medicală?
            </Label>
            <Select
              value={internariMedicale}
              onValueChange={(value) => setValue("istoric_medical.internari_medicale", value)}
            >
              <SelectTrigger data-testid="select-internari">
                <SelectValue placeholder="Selectați" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Nu">Nu</SelectItem>
                <SelectItem value="Da">Da</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {internariMedicale === "Da" && (
            <div className="space-y-2 pl-4 border-l-2 border-primary/30">
              <Label htmlFor="internari_medicale_detalii">Specificați motivul internării</Label>
              <Textarea
                id="internari_medicale_detalii"
                data-testid="input-internari-detalii"
                placeholder="ex: spitalizare pentru apendicectomie în aprilie 2026"
                rows={2}
                {...register("istoric_medical.internari_medicale_detalii")}
              />
            </div>
          )}

          {/* Tratament medicamentos */}
          <div className="space-y-2">
            <Label htmlFor="tratament_medicamentos">Urmați un tratament medicamentos?</Label>
            <Select
              value={tratament}
              onValueChange={(value) => setValue("istoric_medical.tratament_medicamentos", value)}
            >
              <SelectTrigger data-testid="select-tratament">
                <SelectValue placeholder="Selectați" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Nu">Nu</SelectItem>
                <SelectItem value="Da">Da</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {tratament === "Da" && (
            <div className="space-y-2 pl-4 border-l-2 border-primary/30">
              <Label htmlFor="tratament_detalii">Specificați tratamentul urmat</Label>
              <Textarea
                id="tratament_detalii"
                data-testid="input-tratament-detalii"
                placeholder="ex: Beta-blocant 1 cp/zi pentru hipertensiune"
                rows={2}
                {...register("istoric_medical.tratament_detalii")}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Efort Fizic */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            <Activity className="w-5 h-5 text-primary" />
            Toleranță la Efort Fizic
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="schimbari_efort">
              Prezentați schimbări ale stării de sănătate în timpul efortului sau imediat după efort?
            </Label>
            <div
              className="inline-flex rounded-md border overflow-hidden"
              role="radiogroup"
              aria-label="Schimbări la efort"
              data-testid="select-schimbari-efort"
            >
              <button
                type="button"
                role="radio"
                aria-checked={schimbariEfort === "Nu"}
                onClick={() => setValue("efort_fizic.schimbari_efort", "Nu", { shouldValidate: true, shouldDirty: true })}
                className={`px-4 py-2 text-sm font-medium transition ${schimbariEfort === "Nu" ? "bg-slate-700 text-white" : "bg-white hover:bg-slate-50 text-slate-700"}`}
                data-testid="schimbari-efort-nu"
              >
                Nu
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={schimbariEfort === "Da"}
                onClick={() => setValue("efort_fizic.schimbari_efort", "Da", { shouldValidate: true, shouldDirty: true })}
                className={`px-4 py-2 text-sm font-medium border-l transition ${schimbariEfort === "Da" ? "bg-amber-600 text-white" : "bg-white hover:bg-amber-50 text-amber-700"}`}
                data-testid="schimbari-efort-da"
              >
                Da
              </button>
            </div>
          </div>

          {schimbariEfort === "Da" && (
            <div className="space-y-3 rounded-md border p-3 bg-slate-50" data-testid="schimbari-efort-tipuri-container">
              <p className="text-sm font-medium">Ce tip de schimbări?</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {SCHIMBARI_EFORT_OPTIONS.map((opt) => {
                  const tipuri = watch("efort_fizic.schimbari_efort_tipuri") || [];
                  const checked = tipuri.includes(opt);
                  const idAttr = `schimbari-efort-opt-${opt.substring(0, 20).replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`;
                  return (
                    <div key={opt} className="flex items-start gap-2">
                      <Checkbox
                        id={idAttr}
                        checked={checked}
                        onCheckedChange={(v) => {
                          const current = watch("efort_fizic.schimbari_efort_tipuri") || [];
                          const isOn = !!v;
                          const next = isOn
                            ? [...current.filter((x) => x !== opt), opt]
                            : current.filter((x) => x !== opt);
                          setValue("efort_fizic.schimbari_efort_tipuri", next, { shouldDirty: true });
                          // Dacă "Altele" e debifat, golim și câmpul liber
                          if (opt === "Altele" && !isOn) {
                            setValue("efort_fizic.schimbari_efort_detalii", "", { shouldDirty: true });
                          }
                        }}
                        data-testid={idAttr}
                      />
                      <Label htmlFor={idAttr} className="text-sm font-normal leading-snug cursor-pointer">
                        {opt}
                      </Label>
                    </div>
                  );
                })}
              </div>
              {(watch("efort_fizic.schimbari_efort_tipuri") || []).includes("Altele") && (
                <div className="space-y-1 pt-1" data-testid="schimbari-efort-detalii-container">
                  <Label htmlFor="schimbari_efort_detalii" className="text-sm">Specificați schimbările</Label>
                  <Input
                    id="schimbari_efort_detalii"
                    data-testid="input-schimbari-efort-detalii"
                    placeholder="Descrieți alte simptome..."
                    {...register("efort_fizic.schimbari_efort_detalii")}
                  />
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="toleranta_efort">
              Cum tolerați efortul fizic în cadrul competițiilor sportive și antrenamentelor sportive?
            </Label>
            <Select
              value={watch("efort_fizic.toleranta_efort")}
              onValueChange={(value) => setValue("efort_fizic.toleranta_efort", value)}
            >
              <SelectTrigger data-testid="select-toleranta">
                <SelectValue placeholder="Selectați" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Foarte bine">Foarte bine</SelectItem>
                <SelectItem value="Bine">Bine</SelectItem>
                <SelectItem value="Moderat">Moderat</SelectItem>
                <SelectItem value="Slab">Slab</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="suplimente">
              Folosiți suplimente nutritive în scopul îmbunătățirii performanțelor sportive?
            </Label>
            <Select
              value={watch("efort_fizic.suplimente_nutritive")}
              onValueChange={(value) => setValue("efort_fizic.suplimente_nutritive", value)}
            >
              <SelectTrigger data-testid="select-suplimente">
                <SelectValue placeholder="Selectați" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Nu">Nu</SelectItem>
                <SelectItem value="Da">Da</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="oboseste">
              Obosiți mai repede decât ceilalți în timpul antrenamentelor?
            </Label>
            <Select
              value={watch("efort_fizic.oboseste_repede")}
              onValueChange={(value) => setValue("efort_fizic.oboseste_repede", value)}
            >
              <SelectTrigger data-testid="select-oboseste">
                <SelectValue placeholder="Selectați" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Nu">Nu</SelectItem>
                <SelectItem value="Da">Da</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Informații Seniori (condițional — apare doar dacă "Seniori" este bifat) */}
      {seniori && (
        <Card className="border-primary/40 bg-primary/5" data-testid="card-info-seniori">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: "'Work Sans', sans-serif" }}>
              <Trophy className="w-5 h-5 text-primary" />
              Informații Seniori
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* 1. Vechime activitate sportivă */}
            <div className="space-y-2">
              <Label htmlFor="vechime_sportiva">
                1. Menționați vechimea în activitatea sportivă
              </Label>
              <Input
                id="vechime_sportiva"
                data-testid="input-vechime-sportiva"
                placeholder="ex: 8 ani"
                {...register("info_seniori.vechime_sportiva")}
              />
            </div>

            {/* 2. Alte sporturi de performanță */}
            <div className="space-y-2">
              <Label htmlFor="alte_sporturi">
                2. Ați practicat și alte sporturi de performanță?
              </Label>
              <Select
                value={alteSporturi || ""}
                onValueChange={(value) => setValue("info_seniori.alte_sporturi", value)}
              >
                <SelectTrigger data-testid="select-alte-sporturi">
                  <SelectValue placeholder="Selectați" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Nu">Nu</SelectItem>
                  <SelectItem value="Da">Da</SelectItem>
                </SelectContent>
              </Select>
              {alteSporturi === "Da" && (
                <Input
                  data-testid="input-alte-sporturi-detalii"
                  placeholder="Specifică"
                  {...register("info_seniori.alte_sporturi_detalii")}
                />
              )}
            </div>

            {/* 3. Ultimul club */}
            <div className="space-y-2">
              <Label htmlFor="ultimul_club">
                3. Menționați ultimul club la care ați activat
              </Label>
              <Input
                id="ultimul_club"
                data-testid="input-ultimul-club"
                placeholder="Numele clubului"
                {...register("info_seniori.ultimul_club")}
              />
            </div>

            {/* 4. Ultimul control medico-sportiv */}
            <div className="space-y-2">
              <Label htmlFor="ultimul_control">
                4. Menționați data și locul ultimului control medico-sportiv?
              </Label>
              <Input
                id="ultimul_control"
                data-testid="input-ultimul-control"
                placeholder="ex: 15.06.2025 - INMS București"
                {...register("info_seniori.ultimul_control")}
              />
            </div>

            {/* 5. Tulburări apetit */}
            <div className="space-y-2">
              <Label htmlFor="tulburari_apetit">
                5. Prezentați tulburări ale apetitului?
              </Label>
              <Select
                value={tulburariApetit || ""}
                onValueChange={(value) => setValue("info_seniori.tulburari_apetit", value)}
              >
                <SelectTrigger data-testid="select-tulburari-apetit">
                  <SelectValue placeholder="Selectați" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Nu">Nu</SelectItem>
                  <SelectItem value="Da">Da</SelectItem>
                </SelectContent>
              </Select>
              {tulburariApetit === "Da" && (
                <Input
                  data-testid="input-tulburari-apetit-detalii"
                  placeholder="Specifică"
                  {...register("info_seniori.tulburari_apetit_detalii")}
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Declarații */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            Declarații și Consimțământ <span className="text-destructive">*</span>
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Toate cele 3 declarații de mai jos sunt obligatorii pentru a putea trimite formularul.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-start space-x-3">
              <Checkbox
                id="declaratie_boli"
                data-testid="checkbox-declaratie-boli"
                checked={watch("declaratii.declaratie_boli")}
                onCheckedChange={(checked) => setValue("declaratii.declaratie_boli", checked, { shouldValidate: true })}
                className={errors.declaratii?.declaratie_boli ? "border-destructive" : ""}
              />
              <Label htmlFor="declaratie_boli" className="text-sm leading-relaxed cursor-pointer">
                Declar pe proprie răspundere că nu mă aflu în evidență cu boli neurologice (Boala Parkinson, epilepsie), 
                afecțiuni psihiatrice (depresie, schizofrenie, sindrom bipolar) sau alte boli care pun în pericol 
                sănătatea celorlalți. <span className="text-destructive">*</span>
              </Label>
            </div>
            {errors.declaratii?.declaratie_boli && (
              <p className="text-sm text-destructive mt-1 ml-7" data-testid="error-declaratie-boli">
                {errors.declaratii.declaratie_boli.message}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-start space-x-3">
              <Checkbox
                id="acord_date"
                data-testid="checkbox-acord-date"
                checked={watch("declaratii.acord_date_personale")}
                onCheckedChange={(checked) => setValue("declaratii.acord_date_personale", checked, { shouldValidate: true })}
                className={errors.declaratii?.acord_date_personale ? "border-destructive" : ""}
              />
              <Label htmlFor="acord_date" className="text-sm leading-relaxed cursor-pointer">
                SUNT DE ACORD ÎN LEGĂTURĂ CU PRELUCRAREA DATELOR CU CARACTER PERSONAL ÎN VEDEREA EFECTUĂRII 
                SERVICIILOR MEDICALE. <span className="text-destructive">*</span>
              </Label>
            </div>
            {errors.declaratii?.acord_date_personale && (
              <p className="text-sm text-destructive mt-1 ml-7" data-testid="error-acord-date">
                {errors.declaratii.acord_date_personale.message}
              </p>
            )}
          </div>

          <div>
            <div className="flex items-start space-x-3">
              <Checkbox
                id="cunostinta"
                data-testid="checkbox-cunostinta"
                checked={watch("declaratii.cunostinta_anulare")}
                onCheckedChange={(checked) => setValue("declaratii.cunostinta_anulare", checked, { shouldValidate: true })}
                className={errors.declaratii?.cunostinta_anulare ? "border-destructive" : ""}
              />
              <Label htmlFor="cunostinta" className="text-sm leading-relaxed cursor-pointer">
                Am luat la cunoștință că orice modificare a stării de sănătate anulează acest aviz medico-sportiv. <span className="text-destructive">*</span>
              </Label>
            </div>
            {errors.declaratii?.cunostinta_anulare && (
              <p className="text-sm text-destructive mt-1 ml-7" data-testid="error-cunostinta">
                {errors.declaratii.cunostinta_anulare.message}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Slot pentru secțiunea de atașamente (fișiere/poze) — injectat de părinte în modul public */}
      {attachmentsSlot}

      {/* Sumar erori validare — afișat când utilizatorul încearcă să trimită formularul cu câmpuri lipsă */}
      {errorSummary.length > 0 && (
        <div
          className="rounded-md border-2 border-destructive bg-destructive/5 p-4 space-y-2 animate-fade-in"
          data-testid="form-error-summary"
          role="alert"
        >
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-full bg-destructive flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-sm font-bold">!</span>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-destructive">
                Nu se poate trimite formularul — {errorSummary.length} {errorSummary.length === 1 ? "câmp necompletat" : "câmpuri necompletate"}
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                Completați câmpurile de mai jos înainte de a continua:
              </p>
              <ul className="mt-2 space-y-1 list-disc list-inside text-sm">
                {errorSummary.map((e, idx) => (
                  <li key={`${e.field}-${idx}`} className="text-destructive">
                    <strong>{e.field}</strong>: {e.message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button — centrat pentru a evita suprapunerea cu watermark-ul Emergent pe mobil */}
      <div className="flex justify-center pb-16 sm:pb-8">
        <Button
          type="submit"
          data-testid="submit-patient-form"
          disabled={isLoading}
          className="min-w-[220px]"
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
